import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { Device, DeviceSummary } from '../lib/api';
import {
  Empty,
  ErrorNotice,
  Loading,
  ManagementBadge,
  Panel,
  PolicyStatusBadge,
  ReachabilityBadge,
  StudentCell,
  relativeTime,
  useAsync,
} from '../components/ui';
import { BatteryMeter, KioskSwitch, MetricCard } from '../components/chrome';
import { IconFilter, IconPlus, IconTablet, IconWarn, IconWifi, IconWifiOff } from '../components/icons';

export default function DevicesPage() {
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [status, setStatus] = useState('all');
  const [management, setManagement] = useState('all');
  const [policy, setPolicy] = useState('all');
  const [page, setPage] = useState(1);
  const [rowChoice, setRowChoice] = useState('5');
  const [customRows, setCustomRows] = useState('25');

  /**
   * How many devices to show at once.
   *
   * Five by default: an invigilator opening this page is usually chasing one
   * tablet, not reading the whole fleet, and a short table puts the filters and
   * the first rows on screen together. The custom box is parsed defensively
   * because the field can legitimately be mid-edit and empty.
   */
  const parsedCustom = Number.parseInt(customRows, 10);
  const pageSize =
    rowChoice === 'custom'
      ? Number.isFinite(parsedCustom)
        ? Math.min(200, Math.max(1, parsedCustom))
        : 5
      : Number(rowChoice);

  const { data, error, loading, reload } = useAsync(
    (signal) =>
      api.get<{ devices: Device[]; summary: DeviceSummary }>(
        `/devices${search ? `?search=${encodeURIComponent(search)}` : ''}`,
        signal,
      ),
    [search],
    15_000,
  );

  const summary = data?.summary;
  const filtered = useMemo(() => {
    let rows = data?.devices ?? [];
    if (status !== 'all') rows = rows.filter((d) => d.online === status);
    if (management !== 'all') rows = rows.filter((d) => d.managementState === management);
    if (policy !== 'all') {
      rows = rows.filter((d) => (d.policy?.status ?? 'none') === policy);
    }
    return rows;
  }, [data, status, management, policy]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // Raising the row count can strand the reader past the last page, so the
  // current page is clamped on render rather than corrected afterwards.
  const current = Math.min(page, pages);
  const first = (current - 1) * pageSize;
  const slice = filtered.slice(first, first + pageSize);

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Devices</h2>
          <p>Every enrolled tablet, who is holding it, and the policy it has actually applied.</p>
        </div>
        <Link className="button primary" to="/enrollment"><IconPlus /> Add device</Link>
      </div>

      <ErrorNotice error={error} onRetry={reload} />

      <div className="metric-row">
        <MetricCard tone="blue" icon={<IconTablet />} label="Total Devices" value={summary?.total ?? '—'} />
        <MetricCard tone="green" icon={<IconWifi />} label="Online" value={summary?.online ?? '—'} />
        <MetricCard tone="red" icon={<IconWifiOff />} label="Offline" value={summary?.offline ?? '—'} />
        <MetricCard tone="orange" icon={<IconWarn />} label="Needs Attention" value={summary?.needsAttention ?? '—'} />
      </div>

      <Panel>
        <div className="filter-bar">
          <input
            type="search"
            placeholder="Search by name, model, asset tag or serial…"
            aria-label="Search devices"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="Status">
            <option value="all">Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="never_seen">Never seen</option>
          </select>
          <select value={management} onChange={(e) => { setManagement(e.target.value); setPage(1); }} aria-label="Management">
            <option value="all">Management</option>
            <option value="device_owner">Device Owner</option>
            <option value="unmanaged">Unmanaged</option>
          </select>
          <select value={policy} onChange={(e) => { setPolicy(e.target.value); setPage(1); }} aria-label="Policy">
            <option value="all">Policy</option>
            <option value="applied">Applied</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="failed">Not enforced</option>
          </select>
          <select
            className="page-size"
            value={rowChoice}
            onChange={(e) => { setRowChoice(e.target.value); setPage(1); }}
            aria-label="Devices per page"
          >
            <option value="5">Show 5</option>
            <option value="10">Show 10</option>
            <option value="20">Show 20</option>
            <option value="custom">Custom…</option>
          </select>
          {rowChoice === 'custom' && (
            <input
              className="page-size-custom"
              type="number"
              min={1}
              max={200}
              value={customRows}
              onChange={(e) => { setCustomRows(e.target.value); setPage(1); }}
              aria-label="Devices per page (custom)"
            />
          )}
          <button type="button" className="small" disabled>
            <IconFilter /> More filters
          </button>
        </div>

        {loading && !data ? (
          <Loading what="devices" />
        ) : slice.length === 0 ? (
          <Empty title={search ? 'No devices match that search.' : 'No devices enrolled yet.'}>
            {search ? (
              'Try a student name, a model, an asset tag or a serial number.'
            ) : (
              <>
                <Link to="/enrollment">Generate an enrollment token</Link> to add the first one.
              </>
            )}
          </Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Device</th>
                    <th>Status</th>
                    <th>Management</th>
                    <th>Policy</th>
                    <th>Kiosk</th>
                    <th>Battery</th>
                    <th>Last check-in</th>
                  </tr>
                </thead>
                <tbody>
                  {slice.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <Link to={`/devices/${d.id}`}>
                          <StudentCell student={d.student} />
                        </Link>
                      </td>
                      <td>
                        <div className="device-cell">
                          <span className="model">
                            {[d.manufacturer, d.model].filter(Boolean).join(' ') || d.displayName}
                          </span>
                          {d.serialNumber && <span className="serial">{d.serialNumber}</span>}
                        </div>
                      </td>
                      <td><ReachabilityBadge reachability={d.reachability} /></td>
                      <td><ManagementBadge state={d.managementState} /></td>
                      <td>
                        {d.policy ? (
                          <>
                            <PolicyStatusBadge status={d.policy.status} />
                            <div className="small muted">{d.policy.name}</div>
                          </>
                        ) : (
                          <span className="badge muted">None</span>
                        )}
                      </td>
                      <td><KioskSwitch on={d.kioskActive} /></td>
                      <td><BatteryMeter level={d.battery?.level ?? null} /></td>
                      <td className="small muted">{relativeTime(d.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-foot">
              <span className="small muted">
                Showing {first + 1}–{first + slice.length} of {filtered.length} devices
              </span>
              <div className="pager">
                <button type="button" className="small" disabled={current <= 1} onClick={() => setPage(current - 1)}>‹</button>
                <span className="page-num">{current}</span>
                <button type="button" className="small" disabled={current >= pages} onClick={() => setPage(current + 1)}>›</button>
              </div>
            </div>
          </>
        )}
      </Panel>
    </>
  );
}
